import { Stack, Typography, Box, Link as MuiLink } from "@mui/material";
import { Download, Extension, CheckCircle } from "@mui/icons-material";

interface FluxSignerInstallationStepsProps {
  currentStep: number;
}

/**
 * Componente que exibe os passos de instalação do FluxSigner
 * Usado tanto no modal de ativação quanto no manual de instalação
 */
export function FluxSignerInstallationSteps({ currentStep }: FluxSignerInstallationStepsProps) {
  return (
    <>
      {/* Step 1: Instalar extensão do Chrome */}
      {currentStep === 1 && (
        <Stack spacing={3} mt={2}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Extension fontSize="large" color="primary" />
            <Typography variant="h6">
              Passo 1: Instalar Extensão do Chrome
            </Typography>
          </Stack>
          
          <Typography variant="body1">
            Para usar o FluxSigner, você precisa primeiro instalar a extensão oficial do Chrome.
          </Typography>
          
          <Box
            sx={{
              p: 3,
              borderRadius: 2,
              border: "2px solid",
              borderColor: "primary.main",
            }}
          >
            <Typography variant="body2" fontWeight="bold" gutterBottom>
              📌 Instruções:
            </Typography>
            <ol style={{ marginLeft: 20, marginTop: 8 }}>
              <li>
                <Typography variant="body2">
                  Clique no link abaixo para abrir a Chrome Web Store
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  Clique em "Adicionar ao Chrome"
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  Confirme a instalação quando solicitado
                </Typography>
              </li>
            </ol>
            
            <MuiLink
              href="https://chromewebstore.google.com/detail/fluxsigner-assinatura-dig/gmioenipelgmngkedlphkofnbkjdfigk"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: "inline-block",
                mt: 2,
                fontSize: "1rem",
                fontWeight: "bold",
                textDecoration: "none",
              }}
            >
              🔗 Abrir Chrome Web Store - FluxSigner
            </MuiLink>
          </Box>
        </Stack>
      )}

      {/* Step 2: Baixar e instalar executável do Windows */}
      {currentStep === 2 && (
        <Stack spacing={3} mt={2}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Download fontSize="large" color="primary" />
            <Typography variant="h6">
              Passo 2: Instalar Aplicativo Windows
            </Typography>
          </Stack>
          
          <Typography variant="body1">
            Agora você precisa instalar o aplicativo nativo do FluxSigner no Windows para comunicação com os certificados digitais.
          </Typography>
          
          <Box
            sx={{
              p: 3,
              borderRadius: 2,
              border: "2px solid",
              borderColor: "secondary.main",
            }}
          >
            <Typography variant="body2" fontWeight="bold" gutterBottom>
              📌 Instruções:
            </Typography>
            <ol style={{ marginLeft: 20, marginTop: 8 }}>
              <li>
                <Typography variant="body2">
                  Clique no link abaixo para acessar a página de downloads
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  Baixe o arquivo <strong>FluxSignerSetup-2.1.6.exe</strong>
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  Execute o instalador e siga as instruções
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  Reinicie o navegador Chrome após a instalação
                </Typography>
              </li>
            </ol>
            
            <MuiLink
              href="https://github.com/fluxmed/fluxsigner-support/releases/tag/v2.1.6"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: "inline-block",
                mt: 2,
                fontSize: "1rem",
                fontWeight: "bold",
                textDecoration: "none",
              }}
            >
              🔗 Baixar FluxSignerSetup-2.1.6.exe
            </MuiLink>
          </Box>
        </Stack>
      )}

      {/* Step 3: Confirmação e ativação */}
      {currentStep === 3 && (
        <Stack spacing={3} mt={2}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <CheckCircle fontSize="large" color="success" />
            <Typography variant="h6">
              Passo 3: Confirmar e Ativar
            </Typography>
          </Stack>
          
          <Typography variant="body1">
            Confirme que você completou todos os passos anteriores:
          </Typography>
          
          <Box
            sx={{
              p: 3,
              borderRadius: 2,
              border: "2px solid",
              borderColor: "success.main",
            }}
          >
            <Typography variant="body2" fontWeight="bold" gutterBottom>
              ✅ Checklist de Instalação:
            </Typography>
            <Stack spacing={1} mt={2}>
              <Stack direction="row" spacing={1} alignItems="center">
                <CheckCircle fontSize="small" color="success" />
                <Typography variant="body2">
                  Extensão do Chrome instalada
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <CheckCircle fontSize="small" color="success" />
                <Typography variant="body2">
                  Aplicativo Windows instalado (FluxSignerSetup-2.1.6.exe)
                </Typography>
              </Stack>
            </Stack>
          </Box>

          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              border: "2px solid",
              borderColor: "warning.main",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              <strong>Importante:</strong> Ao clicar em "Finalizar", o FluxSigner será ativado na sua conta e você poderá usar a assinatura digital do computador para assinar documentos médicos.
            </Typography>
          </Box>
        </Stack>
      )}
    </>
  );
}

